/**
 * Interactive mockup of the Postmaster reputation/spam-rate trend chart that
 * lives in the authed dashboard. Hover the line to see per-day values; the
 * card recomputes the headline number on hover so it feels responsive.
 *
 * Data is seeded from a deterministic random walk (no live data here) so the
 * chart looks like a real production domain — small daily wobbles, one
 * "incident dip" the recovery line clearly fixes. Don't use real customer
 * data on a marketing surface.
 */
import { useMemo, useState } from 'react';

const W = 600;
const H = 150;
const PAD_X = 16;
const PAD_Y = 14;

interface DayPoint {
  day: number; // 0 = today, -29 = 30 days ago
  reputation: number; // 0-100
  spamRate: number; // 0-1
}

function seedSeries(): DayPoint[] {
  // Deterministic — same numbers on every render so the visual is stable.
  // 30 days, rep starts at 78, wobbles, dips at -10 (incident), recovers.
  const out: DayPoint[] = [];
  let rep = 78;
  let spam = 0.0021;
  for (let i = 29; i >= 0; i -= 1) {
    // Tiny wobble
    const wobble = ((i * 73) % 7) / 100 - 0.03;
    rep = Math.max(50, Math.min(95, rep + wobble * 3));
    spam = Math.max(0, Math.min(0.01, spam + wobble * 0.0007));
    // Incident at -10: rep drops, spam spikes, recovers over 4 days
    if (i === 10) {
      rep = 64;
      spam = 0.0064;
    }
    if (i < 10 && i >= 6) {
      rep = Math.min(85, rep + 4);
      spam = Math.max(0.0015, spam - 0.0011);
    }
    if (i < 6) {
      rep = Math.min(92, rep + 0.5);
      spam = Math.max(0.0012, spam - 0.0001);
    }
    out.push({ day: -i, reputation: Math.round(rep * 10) / 10, spamRate: Math.round(spam * 10000) / 10000 });
  }
  return out;
}

function pathFromSeries(series: DayPoint[], yKey: keyof DayPoint, yMin: number, yMax: number): string {
  const xStep = (W - PAD_X * 2) / (series.length - 1);
  return series
    .map((p, i) => {
      const x = PAD_X + i * xStep;
      const y = PAD_Y + ((yMax - (p[yKey] as number)) / (yMax - yMin)) * (H - PAD_Y * 2);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

export function PostmasterMockup() {
  const series = useMemo(seedSeries, []);
  const [hover, setHover] = useState<number | null>(null);
  const idx = hover ?? series.length - 1;
  const cur = series[idx];
  const repPath = pathFromSeries(series, 'reputation', 50, 100);
  const spamPath = pathFromSeries(series, 'spamRate', 0, 0.008);

  const xStep = (W - PAD_X * 2) / (series.length - 1);
  const hoverX = PAD_X + idx * xStep;

  return (
    <div className="overflow-hidden rounded-lg border bg-background/80 shadow-lg ring-1 ring-border/40">
      <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-3 text-xs font-mono uppercase tracking-wider text-muted-foreground">
        <span>postmaster.google.com · sending domain</span>
        <span className="flex items-center gap-1.5 rounded bg-emerald-500/10 px-2 py-0.5 text-emerald-700 dark:text-emerald-400">
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
          live · synced 2h ago
        </span>
      </div>
      <div className="px-5 py-4">
        <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Reputation</div>
            <div className="font-mono text-4xl font-semibold tabular-nums">
              {cur.reputation.toFixed(1)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Spam rate</div>
            <div className="font-mono text-2xl font-semibold tabular-nums text-warning">
              {(cur.spamRate * 100).toFixed(2)}%
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">DKIM pass</div>
            <div className="font-mono text-2xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">99.4%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">SPF pass</div>
            <div className="font-mono text-2xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">99.9%</div>
          </div>
          {/* Fixed width + no-wrap so scrubbing between "today" and
              "29 days ago" never re-wraps this row and shifts the chart below. */}
          <div className="ml-auto w-24 shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">
            {cur.day === 0 ? 'today' : `${Math.abs(cur.day)} days ago`}
          </div>
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="mt-3 h-[150px] w-full cursor-crosshair"
          onMouseLeave={() => setHover(null)}
          onMouseMove={(e) => {
            const rect = (e.target as SVGElement).getBoundingClientRect();
            const relX = ((e.clientX - rect.left) / rect.width) * W;
            const i = Math.max(0, Math.min(series.length - 1, Math.round((relX - PAD_X) / xStep)));
            setHover(i);
          }}
        >
          {/* gridlines */}
          {[0.25, 0.5, 0.75].map((p) => (
            <line
              key={p}
              x1={PAD_X}
              x2={W - PAD_X}
              y1={PAD_Y + p * (H - PAD_Y * 2)}
              y2={PAD_Y + p * (H - PAD_Y * 2)}
              stroke="currentColor"
              strokeOpacity={0.07}
              strokeDasharray="2 3"
            />
          ))}
          {/* spam-rate (warning, scaled to top half) */}
          <path d={spamPath} fill="none" stroke="hsl(var(--warning))" strokeWidth={2} strokeOpacity={0.65} />
          {/* reputation (primary) */}
          <path d={repPath} fill="none" stroke="hsl(var(--primary))" strokeWidth={2.5} />
          {/* hover crosshair */}
          {hover !== null && (
            <>
              <line x1={hoverX} x2={hoverX} y1={PAD_Y} y2={H - PAD_Y} stroke="currentColor" strokeOpacity={0.22} strokeDasharray="3 3" />
              <circle cx={hoverX} cy={PAD_Y + ((100 - cur.reputation) / 50) * (H - PAD_Y * 2)} r={5} fill="hsl(var(--primary))" />
            </>
          )}
        </svg>
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>30d ago</span>
          <span className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-4 rounded bg-primary" /> Reputation
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-4 rounded bg-warning" /> Spam rate
            </span>
          </span>
          <span>today</span>
        </div>
        <div className="mt-3 flex items-center justify-between border-t pt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
          <span>Hover the chart to scrub the day</span>
          <span className="text-primary">↑ from 64.0 reputation 7d ago</span>
        </div>
      </div>
    </div>
  );
}
